const API_URL = import.meta.env.VITE_API_URL ?? 'https://api-staging.aasshield.com';

type Result<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };

async function request<T>(path: string, token: string, init?: RequestInit): Promise<Result<T>> {
  try {
    const res = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...init?.headers,
      },
    });

    const json = await res.json();

    if (!res.ok) {
      return { ok: false, error: json.error ?? { code: 'API_ERROR', message: `HTTP ${res.status}` } };
    }

    return { ok: true, data: json.data };
  } catch {
    return { ok: false, error: { code: 'NETWORK_ERROR', message: 'Failed to connect' } };
  }
}

async function requestPublic<T>(path: string): Promise<Result<T>> {
  try {
    const res = await fetch(`${API_URL}${path}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    if (!res.ok) {
      return { ok: false, error: json.error ?? { code: 'API_ERROR', message: `HTTP ${res.status}` } };
    }
    return { ok: true, data: json.data };
  } catch {
    return { ok: false, error: { code: 'NETWORK_ERROR', message: 'Failed to connect' } };
  }
}

export function getInviteInfo(inviteToken: string) {
  return requestPublic<any>(`/api/v1/team/join/${inviteToken}`);
}

export function createApi(token: string) {
  return {
    getAnalyses(page = 1, limit = 20) {
      return request<any>(`/api/v1/analyses?page=${page}&limit=${limit}`, token);
    },
    getSettings() {
      return request<any>('/api/v1/settings', token);
    },
    updateSettings(settings: object) {
      return request<any>('/api/v1/settings', token, {
        method: 'PATCH',
        body: JSON.stringify(settings),
      });
    },
    getUsage() {
      return request<any>('/api/v1/usage', token);
    },
    getWeeklyReport() {
      return request<any>('/api/v1/reports/weekly', token);
    },
    getTeam() {
      return request<any>('/api/v1/team', token);
    },
    createTeam(name: string) {
      return request<any>('/api/v1/team', token, {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
    },
    inviteTeamMember(email: string) {
      return request<any>('/api/v1/team/invite', token, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },
    generateInviteLink() {
      return request<{ invite_url: string }>('/api/v1/team/invite-link', token, {
        method: 'POST',
      });
    },
    joinTeam(inviteToken: string) {
      return request<any>(`/api/v1/team/join/${inviteToken}`, token, {
        method: 'POST',
      });
    },
    leaveTeam() {
      return request<{ left: boolean }>('/api/v1/team/leave', token, {
        method: 'POST',
      });
    },
    removeTeamMember(userId: string) {
      return request<{ removed: boolean; user_id: string }>(`/api/v1/team/members/${userId}`, token, {
        method: 'DELETE',
      });
    },
    getSenders() {
      return request<any[]>('/api/v1/senders', token);
    },
    getSenderAnalyses(senderId: string, limit = 20) {
      return request<any[]>(`/api/v1/senders/${senderId}/analyses?limit=${limit}`, token);
    },
    createCheckout(tier: 'fortress' | 'citadel') {
      return request<{ checkout_url: string }>('/api/v1/checkout', token, {
        method: 'POST',
        body: JSON.stringify({ tier }),
      });
    },
    verifyCheckout(sessionId: string) {
      return request<{ synced: boolean; tier?: string; reason?: string }>('/api/v1/checkout/verify', token, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      });
    },
    getSubscription() {
      return request<{ tier: string; subscription: { status: string; current_period_end: string; cancel_at_period_end: boolean; scheduled_change: string | null } | null }>('/api/v1/subscription', token);
    },
    getBillingPortal() {
      return request<{ portal_url: string }>('/api/v1/billing/portal', token, {
        method: 'POST',
      });
    },
    deleteAccount() {
      return request<{ deleted: boolean }>('/api/v1/user', token, {
        method: 'DELETE',
      });
    },
    exportData() {
      return request<Record<string, unknown>>('/api/v1/user/export', token);
    },
  };
}
